import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Plus, Pencil, Trash2, ToggleLeft, ToggleRight } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AirlinesManagement = () => {
  const [airlines, setAirlines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAirline, setEditingAirline] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    baggage_allowance: 30,
    price: 0
  });

    const handleToggleActive = async (itemId, currentStatus) => {
    try {
      await axios.put(`${API}/master/airlines/${itemId}`, { is_active: !currentStatus });
      toast.success('Status berhasil diubah');
      fetchAirlines();
    } catch (error) {
      toast.error('Gagal mengubah status');
    }
  };

const fetchAirlines = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/master/airlines?show_all=true`);
      setAirlines(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching airlines:', error);
      }
      toast.error('Gagal memuat data airlines');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAirlines();
  }, [fetchAirlines]);

  const handleOpenModal = (airline = null) => {
    if (airline) {
      setEditingAirline(airline);
      setFormData({
        name: airline.name || '',
        code: airline.code || '',
        baggage_allowance: airline.baggage_allowance || 30,
        price: airline.price || 0
      });
    } else {
      setEditingAirline(null);
      setFormData({
        name: '',
        code: '',
        baggage_allowance: 30,
        price: 0
      });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const payload = {
      ...formData,
      baggage_allowance: String(formData.baggage_allowance || ''),
      price: parseFloat(formData.price)
    };

    try {
      if (editingAirline) {
        await axios.put(`${API}/master/airlines/${editingAirline.id}`, payload);
        toast.success('Airline berhasil diupdate');
      } else {
        await axios.post(`${API}/master/airlines`, payload);
        toast.success('Airline berhasil ditambahkan');
      }
      setShowModal(false);
      fetchAirlines();
    } catch (error) {
      toast.error(editingAirline ? 'Gagal update airline' : 'Gagal menambah airline');
    }
  };

  const handleDelete = async (airlineId) => {
    if (!window.confirm('Yakin ingin menghapus airline ini?')) return;

    try {
      await axios.delete(`${API}/master/airlines/${airlineId}`);
      toast.success('Airline berhasil dihapus');
      fetchAirlines();
    } catch (error) {
      toast.error('Gagal menghapus airline');
    }
  };

  if (loading) {
    return <div className="text-center py-8">Memuat data...</div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold font-['Outfit']">Airlines Management</h1>
        <Button className="rounded-full" onClick={() => handleOpenModal()}>
          <Plus className="h-4 w-4 mr-2" />
          Tambah Airline
        </Button>
      </div>

      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle>Daftar Maskapai</CardTitle>
        </CardHeader>
        <CardContent>
          {airlines.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              Belum ada data maskapai
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-4 font-semibold">Nama Maskapai</th>
                    <th className="text-left p-4 font-semibold">Kode</th>
                    <th className="text-left p-4 font-semibold">Bagasi</th>
                    <th className="text-left p-4 font-semibold">Harga</th>
                    <th className="text-left p-4 font-semibold">Status</th>
                    <th className="text-right p-4 font-semibold">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {airlines.map((airline) => (
                    <tr key={airline.id} className="border-b hover:bg-muted/50">
                      <td className="p-4 font-medium">{airline.name}</td>
                      <td className="p-4 text-muted-foreground">{airline.code}</td>
                      <td className="p-4">
                        <Badge variant="secondary">{airline.baggage_allowance} kg</Badge>
                      </td>
                      <td className="p-4">
                        Rp {airline.price?.toLocaleString('id-ID')}
                      </td>
                      <td className="p-4">
                        <Badge variant={airline.is_active ? 'default' : 'secondary'}>
                          {airline.is_active ? 'Aktif' : 'Nonaktif'}
                        </Badge>
                      </td>
                      <td className="p-4 text-right">
                        <div className="flex justify-end gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleToggleActive(airline.id, airline.is_active)}
                          >
                            {airline.is_active ? <ToggleRight className="h-4 w-4" /> : <ToggleLeft className="h-4 w-4" />}
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleOpenModal(airline)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="destructive" 
                            size="sm"
                            onClick={() => handleDelete(airline.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingAirline ? 'Edit Airline' : 'Tambah Airline Baru'}
            </DialogTitle>
            <DialogDescription>
              Lengkapi form di bawah untuk {editingAirline ? 'mengupdate' : 'menambahkan'} maskapai
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nama Maskapai *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                    placeholder="Garuda Indonesia"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="code">Kode Maskapai *</Label>
                  <Input
                    id="code"
                    value={formData.code}
                    onChange={(e) => setFormData({...formData, code: e.target.value})}
                    required
                    placeholder="GA"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="baggage_allowance">Bagasi (kg) *</Label>
                  <Input
                    id="baggage_allowance"
                    type="number"
                    value={formData.baggage_allowance}
                    onChange={(e) => setFormData({...formData, baggage_allowance: e.target.value})}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="price">Harga *</Label>
                  <Input
                    id="price"
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData({...formData, price: e.target.value})}
                    required
                  />
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Batal
              </Button>
              <Button type="submit">
                {editingAirline ? 'Update' : 'Tambah'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AirlinesManagement;